import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect, type ReactNode } from "react";

interface ToolbarButtonProps {
  label: string;
  icon: ReactNode;
  isActive?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

function ToolbarButton({
  label,
  icon,
  isActive = false,
  disabled = false,
  onClick,
}: ToolbarButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex size-8 items-center justify-center rounded-xl transition-colors disabled:opacity-50 ${
        isActive
          ? "bg-neutral-100 text-whart-text"
          : "text-neutral-600 hover:bg-neutral-50 hover:text-whart-text"
      }`}
    >
      {icon}
    </button>
  );
}

function IconBold() {
  return (
    <svg aria-hidden viewBox="0 0 16 16" className="size-4" fill="currentColor">
      <path d="M4 2.5h4.1a3.1 3.1 0 0 1 2.2 5.3A3.3 3.3 0 0 1 11.5 13H4V2.5Zm2 2.3V6h2a1.1 1.1 0 1 0 0-2.2H6Zm0 3.4v2.3h2.4a1.15 1.15 0 1 0 0-2.3H6Z" />
    </svg>
  );
}

function IconItalic() {
  return (
    <svg aria-hidden viewBox="0 0 16 16" className="size-4" fill="currentColor">
      <path d="M9.5 3H12l-.5 1h-2l-2 8h2l-.5 1H5l.5-1h2l2-8H6l.5-1h3Z" />
    </svg>
  );
}

function IconUnderline() {
  return (
    <svg
      aria-hidden
      viewBox="0 0 16 16"
      className="size-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path d="M4 2.5v5.2a4 4 0 0 0 8 0V2.5" strokeLinecap="round" />
      <path d="M3.5 13.5h9" strokeLinecap="round" />
    </svg>
  );
}

function IconHeading2() {
  return (
    <svg aria-hidden viewBox="0 0 16 16" className="size-4" fill="currentColor">
      <path d="M2.5 3.5V12h1.8V8.4h3.2V12h1.8V3.5H7.5v3.1H4.3V3.5H2.5Zm8.2 0V12h1.5l2.3-3.4V12H16V3.5h-1.5l-2.3 3.4V3.5h-1.5Z" />
    </svg>
  );
}

function IconHeading3() {
  return (
    <svg aria-hidden viewBox="0 0 16 16" className="size-4" fill="currentColor">
      <path d="M2.5 3.5V12h1.8V8.4h3.2V12h1.8V3.5H7.5v3.1H4.3V3.5H2.5Zm7.4 0h3.3a2.2 2.2 0 1 1 0 4.4H9.9V12h1.8V8.8h.7l1.6 3.2H15l-1.8-3.4a2.3 2.3 0 0 0 1.5-2.1 2.4 2.4 0 0 0-2.4-2.4Z" />
    </svg>
  );
}

function IconBulletList() {
  return (
    <svg
      aria-hidden
      viewBox="0 0 16 16"
      className="size-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path d="M6.5 4h7M6.5 8h7M6.5 12h7" strokeLinecap="round" />
      <circle cx="3.5" cy="4" r="1" fill="currentColor" stroke="none" />
      <circle cx="3.5" cy="8" r="1" fill="currentColor" stroke="none" />
      <circle cx="3.5" cy="12" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

function IconOrderedList() {
  return (
    <svg aria-hidden viewBox="0 0 16 16" className="size-4" fill="currentColor">
      <path d="M6.5 3.8h7v1.2h-7V3.8Zm0 4h7v1.2h-7V7.8Zm0 4h7v1.2h-7v-1.2ZM3.2 3.4l-.7.7.7.7V6l-.7-.7-.7.7h1.1V3.4Zm0 3.8v2.4h1.4v-.9H3.5V7.2Zm-.2 3.9c0-.6.5-1 .9-1.3.4-.3.5-.4.5-.6 0-.2-.2-.4-.5-.4-.3 0-.5.2-.6.5H2.4c.1-.9.8-1.5 1.6-1.5.9 0 1.6.6 1.6 1.4 0 .5-.3.9-.7 1.2l-.4.3c-.3.2-.4.3-.4.5h1.4v.9H2.8v-.9Z" />
    </svg>
  );
}

function IconBlockquote() {
  return (
    <svg
      aria-hidden
      viewBox="0 0 16 16"
      className="size-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path d="M4 3.5v5.5M12 3.5v5.5" strokeLinecap="round" />
      <path d="M2.5 12.5h11" strokeLinecap="round" />
    </svg>
  );
}

interface ArticleRichTextEditorProps {
  editorKey: string;
  value: string;
  placeholder?: string;
  disabled?: boolean;
  labels: {
    toolbar: string;
    bold: string;
    italic: string;
    underline: string;
    heading2: string;
    heading3: string;
    bulletList: string;
    orderedList: string;
    blockquote: string;
  };
  onChange: (html: string) => void;
}

export function ArticleRichTextEditor({
  editorKey,
  value,
  placeholder,
  disabled = false,
  labels,
  onChange,
}: ArticleRichTextEditorProps) {
  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          heading: { levels: [2, 3] },
        }),
        Underline,
        Placeholder.configure({
          placeholder: placeholder ?? "",
        }),
      ],
      content: value,
      editable: !disabled,
      immediatelyRender: false,
      onUpdate: ({ editor: currentEditor }) => {
        onChange(currentEditor.getHTML());
      },
      editorProps: {
        attributes: {
          class: "article-rich-text-editor__content",
        },
      },
    },
    [editorKey],
  );

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  if (!editor) {
    return (
      <div className="article-rich-text-editor wireframe-border bg-whart-bg">
        <div className="min-h-[32rem] animate-pulse bg-neutral-50" />
      </div>
    );
  }

  return (
    <div className="article-rich-text-editor wireframe-border bg-whart-bg">
      <div
        role="toolbar"
        aria-label={labels.toolbar}
        className="flex flex-wrap items-center gap-0.5 border-b border-black/10 px-2 py-1.5"
      >
        <ToolbarButton
          label={labels.bold}
          icon={<IconBold />}
          isActive={editor.isActive("bold")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleBold().run()}
        />
        <ToolbarButton
          label={labels.italic}
          icon={<IconItalic />}
          isActive={editor.isActive("italic")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        />
        <ToolbarButton
          label={labels.underline}
          icon={<IconUnderline />}
          isActive={editor.isActive("underline")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        />
        <span aria-hidden className="mx-1 w-px self-stretch bg-black/10" />
        <ToolbarButton
          label={labels.heading2}
          icon={<IconHeading2 />}
          isActive={editor.isActive("heading", { level: 2 })}
          disabled={disabled}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
        />
        <ToolbarButton
          label={labels.heading3}
          icon={<IconHeading3 />}
          isActive={editor.isActive("heading", { level: 3 })}
          disabled={disabled}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
        />
        <span aria-hidden className="mx-1 w-px self-stretch bg-black/10" />
        <ToolbarButton
          label={labels.bulletList}
          icon={<IconBulletList />}
          isActive={editor.isActive("bulletList")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        />
        <ToolbarButton
          label={labels.orderedList}
          icon={<IconOrderedList />}
          isActive={editor.isActive("orderedList")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        />
        <ToolbarButton
          label={labels.blockquote}
          icon={<IconBlockquote />}
          isActive={editor.isActive("blockquote")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        />
      </div>

      <EditorContent editor={editor} />
    </div>
  );
}
